<template>
  <v-dialog :value="value" max-width="1000" scrollable persistent @input="$emit('input', $event)">
    <v-card>
      <v-toolbar flat dense color="#1d4ed8" dark>
        <v-icon left>mdi-file-document-plus-outline</v-icon>
        <v-toolbar-title class="subtitle-1 font-weight-medium">
          Emitir MDF-e — {{ (grupo && grupo.nome) || 'rota' }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon small :disabled="emitindo" @click="fechar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4 corpo">
        <div class="aviso info mb-3">
          <span class="ic">ℹ️</span>
          <div>
            <strong>{{ chaves.length }} CT-e</strong> marcados nesta rota vão para este MDF-e.
            Para mandar o resto depois, basta emitir outro com os CT-e que ficaram.
          </div>
        </div>

        <!-- ============ VEÍCULO E MOTORISTA ============ -->
        <div class="bloco-titulo">Veículo e motorista</div>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-select
              v-model="idVeiculo" :items="veiculos" item-text="rotulo" item-value="idVeiculo"
              label="Veículo" outlined dense hide-details
              :loading="carregandoCadastros" :disabled="emitindo" @change="limparPrevia" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="idMotorista" :items="motoristas" item-text="rotulo" item-value="idMotorista"
              label="Motorista" outlined dense hide-details
              :loading="carregandoCadastros" :disabled="emitindo" @change="limparPrevia" />
          </v-col>
        </v-row>

        <!-- ============ LOCAL DE CARREGAMENTO ============ -->
        <div class="bloco-titulo mt-4">Local de carregamento</div>
        <div class="ajuda mb-2">
          É o CD, onde a carga está agora — não a origem dos CT-e, que é a outra ponta da
          viagem. Por isso não vem preenchido.
        </div>
        <v-row dense>
          <v-col cols="6" md="3">
            <v-text-field v-model.number="cdCodigoIbge" label="Código IBGE" type="number"
              outlined dense hide-details :disabled="emitindo" @change="limparPrevia" />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field v-model="cdUf" label="UF" maxlength="2" outlined dense hide-details
              :disabled="emitindo" @change="limparPrevia" />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field v-model="cdMunicipio" label="Município" outlined dense hide-details
              :disabled="emitindo" @change="limparPrevia" />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field v-model="cdCep" label="CEP" maxlength="8" outlined dense hide-details
              :disabled="emitindo" @change="limparPrevia" />
          </v-col>
        </v-row>

        <!-- ============ CIOT ============ -->
        <div class="bloco-titulo mt-4">CIOT (opcional)</div>
        <div class="ajuda mb-2">
          Se o motorista for terceiro e o MDF-e sair sem CIOT, o documento fica incompleto na
          pista. Informe o número já gerado, ou deixe em branco.
        </div>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field v-model="ciot" label="Número do CIOT" maxlength="12" counter="12"
              outlined dense hide-details :disabled="emitindo"
              @input="ciot = (ciot || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase()"
              @change="limparPrevia" />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="cpfCnpjCiot" label="CPF/CNPJ do responsável" outlined dense
              hide-details :disabled="emitindo" @change="limparPrevia" />
          </v-col>
        </v-row>

        <!-- ============ FRETE E PISO ANTT ============ -->
        <div class="bloco-titulo mt-4">Frete da perna final</div>
        <div class="ajuda mb-2">
          É o frete desta viagem, não a soma do frete dos CT-e — aquele é da ponta anterior,
          cobrado do cliente. Conferir o piso é opcional e não trava a emissão.
        </div>
        <v-row dense align="center">
          <v-col cols="6" md="3">
            <v-text-field v-model.number="valorFrete" label="Valor do frete (R$)" type="number"
              step="0.01" outlined dense hide-details :disabled="emitindo"
              @change="limparPiso" />
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field v-model.number="distanciaKm" label="Distância (km)" type="number"
              outlined dense hide-details :disabled="emitindo" @change="limparPiso" />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn small outlined :disabled="emitindo || !idVeiculo" @click="conferirPisoAntt">
              <v-icon left small>mdi-calculator</v-icon>Conferir piso ANTT
            </v-btn>
          </v-col>
        </v-row>

        <!--
          Acima do piso nao mostra nada. Aviso que aparece sempre o
          operador para de ler; o unico caso que precisa de texto e
          quando ha algo a dizer.
        -->
        <div v-if="piso && piso.situacao === PISO_ABAIXO" class="aviso atencao mt-2">
          <span class="ic">⚠️</span>
          <div>
            Frete <strong>abaixo do piso ANTT</strong> de {{ moeda(piso.piso) }} —
            faltam {{ moeda(piso.faltam) }}. A emissão segue; a ANTT pode multar.
            <div class="sub">{{ piso.base }}</div>
          </div>
        </div>

        <!--
          Nao calculavel NAO e "esta acima": a tabela B da 6.084/2026 so
          tem coeficiente a partir de 4 eixos, e o caminhao rigido de 2 ou
          3 eixos e o que faz entrega urbana. Silencio aqui o operador
          leria como aprovacao.
        -->
        <div v-else-if="piso && piso.situacao === PISO_NAO_CALCULAVEL" class="aviso info mt-2">
          <span class="ic">ℹ️</span>
          <div>
            <strong>Piso não calculável para esta combinação.</strong> {{ piso.motivo }}
            Não significa que o frete está acima: significa que a conta não foi feita.
          </div>
        </div>

        <!-- ============ PRÉVIA ============ -->
        <div class="fila mt-4">
          <v-btn small color="#1d4ed8" dark :loading="carregandoPrevia" :disabled="emitindo"
            @click="verPrevia">
            <v-icon left small>mdi-magnify</v-icon>Ver prévia
          </v-btn>
          <span class="ajuda ml-2">Monta o MDF-e e mostra o que falta. Não envia nada.</span>
        </div>

        <div v-if="erro" class="aviso erro mt-3">
          <span class="ic">⚠️</span><div>{{ erro }}</div>
        </div>

        <template v-if="previa">
          <div class="totais mt-3">
            <div class="total az">
              <div class="r">CT-e</div><div class="v">{{ previa.quantidadeCtes }}</div>
            </div>
            <div class="total">
              <div class="r">Cidades</div><div class="v">{{ previa.quantidadeCidades }}</div>
            </div>
            <div class="total">
              <div class="r">Peso</div><div class="v">{{ peso(previa.pesoTotal) }}</div>
            </div>
            <div class="total">
              <div class="r">Valor da carga</div>
              <div class="v">{{ moeda(previa.valorCargaTotal) }}</div>
            </div>
          </div>

          <!--
            Impedimentos primeiro: e a lista que o operador tem de
            resolver, e deixa-la abaixo do JSON faria ele rolar para
            descobrir por que o botao esta desabilitado.
          -->
          <div v-if="previa.impedimentos && previa.impedimentos.length" class="aviso erro mt-3">
            <span class="ic">⚠️</span>
            <div>
              <strong>Falta resolver antes de emitir:</strong>
              <ul class="lista">
                <li v-for="(m, i) in previa.impedimentos" :key="'i' + i">{{ m }}</li>
              </ul>
            </div>
          </div>

          <div v-if="previa.suposicoes && previa.suposicoes.length" class="aviso info mt-3">
            <span class="ic">ℹ️</span>
            <div>
              <strong>A rotina escolheu estes valores:</strong>
              <ul class="lista">
                <li v-for="(s, i) in previa.suposicoes" :key="'s' + i">{{ s }}</li>
              </ul>
            </div>
          </div>

          <v-expansion-panels flat class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-header class="text-body-2">
                <span><v-icon left small>mdi-code-json</v-icon> Ver o JSON que seria enviado</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <pre class="json-preview">{{ jsonDaPrevia }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <div v-if="emitido" class="aviso ok mt-3">
          <span class="ic">✅</span>
          <div>
            <strong>MDF-e autorizado.</strong>
            Número {{ emitido.numero }}/{{ emitido.serie }} ·
            {{ emitido.quantidadeCtes }} CT-e embarcados.
            <div class="sub chave">{{ emitido.chaveAcesso }}</div>
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <v-btn text :disabled="emitindo" @click="fechar">
          {{ emitido ? 'Fechar' : 'Cancelar' }}
        </v-btn>
        <v-spacer />
        <!--
          O botao de emitir so aparece depois da previa, e so habilita com
          a previa sem impedimento. Nao e enfeite: do clique em diante o
          documento existe na SEFAZ e nao da para apagar.
        -->
        <v-btn v-if="!emitido" color="success" dark :loading="emitindo" :disabled="!podeEmitir"
          @click="emitir">
          <v-icon left>mdi-send</v-icon>Emitir MDF-e
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import MdfeDistribuicaoService from '../../service/mdfeDistribuicao'
import Transportador from '../../service/transportador'
import {
  formatarPeso, formatarMoeda, conferirPiso,
  PISO_NAO_CALCULAVEL, PISO_ABAIXO
} from '../../utils/mdfeDistribuicao'

/**
 * Emissão do MDF-e de uma rota da rodada.
 *
 * Dois passos, na ordem que a API impõe: ver a prévia, que monta o
 * payload e diz o que falta sem enviar nada; e emitir, que cria
 * documento fiscal de verdade.
 *
 * O botão de emitir só existe depois da prévia e só habilita com a
 * prévia sem impedimento. Qualquer mudança nos campos apaga a prévia —
 * emitir com base numa prévia de outros dados seria pior que não ter
 * prévia nenhuma.
 */
export default {
  name: 'EmitirMdfeDialog',

  props: {
    value: { type: Boolean, default: false },
    idLote: { type: Number, default: null },
    idParticipante: { type: Number, default: null },
    grupo: { type: Object, default: null },
    chaves: { type: Array, default: () => [] }
  },

  data () {
    return {
      veiculos: [],
      motoristas: [],
      carregandoCadastros: false,

      idVeiculo: null,
      idMotorista: null,
      cdCodigoIbge: null,
      cdUf: '',
      cdMunicipio: '',
      cdCep: '',
      ciot: '',
      cpfCnpjCiot: '',

      valorFrete: null,
      distanciaKm: null,
      piso: null,
      PISO_NAO_CALCULAVEL,
      PISO_ABAIXO,

      previa: null,
      carregandoPrevia: false,
      emitindo: false,
      emitido: null,
      erro: ''
    }
  },

  computed: {
    podeEmitir () {
      return !!this.previa &&
        (!this.previa.impedimentos || !this.previa.impedimentos.length)
    },

    jsonDaPrevia () {
      try {
        return JSON.stringify((this.previa && this.previa.payload) || {}, null, 2)
      } catch (e) {
        return '(erro ao formatar: ' + e.message + ')'
      }
    },

    corpo () {
      return {
        idParticipante: this.idParticipante,
        idAgrupador: this.grupo && this.grupo.idAgrupador,
        chaves: this.chaves,
        idVeiculo: this.idVeiculo,
        idMotorista: this.idMotorista,
        cdCodigoIbge: this.cdCodigoIbge || null,
        cdUf: (this.cdUf || '').toUpperCase() || null,
        cdMunicipio: this.cdMunicipio || null,
        cdCep: (this.cdCep || '').replace(/\D/g, '') || null,
        ciot: this.ciot || null,
        cpfCnpjCiot: (this.cpfCnpjCiot || '').replace(/\D/g, '') || null,
        valorFrete: this.valorFrete != null && this.valorFrete !== '' ? this.valorFrete : null,
        // Piso so vai quando foi conferido: nulo no banco significa "nao
        // conferido", que e diferente de "zero" e de "nao calculavel".
        pisoAntt: this.piso ? this.piso.piso : null,
        pisoSituacao: this.piso ? this.piso.situacao : null,
        pisoBase: this.piso ? this.piso.base : null,
        idUsuario: Number(localStorage.getItem('id')) || null
      }
    },

    veiculoSelecionado () {
      return this.veiculos.find(v => v.idVeiculo === this.idVeiculo) || null
    }
  },

  watch: {
    //
    // immediate porque o watcher comum so dispara na mudanca: montado ja
    // aberto, o dialog nao carregaria veiculo nem motorista e o operador
    // veria dois selects vazios sem entender por que.
    //
    value: {
      immediate: true,
      handler (aberto) {
        if (aberto) this.abrir()
      }
    }
  },

  methods: {
    abrir () {
      this.previa = null
      this.piso = null
      this.emitido = null
      this.erro = ''
      this.carregarCadastros()
    },

    fechar () {
      if (this.emitido) this.$emit('emitido', this.emitido)
      this.$emit('input', false)
    },

    //
    // Por idParticipante, e nao por usuario: o MODERADOR opera em nome de
    // um transportador com quem nao tem vinculo, e a lista do usuario
    // traria os veiculos dele, nao os do transportador da rodada.
    //
    async carregarCadastros () {
      if (!this.idParticipante) return
      this.carregandoCadastros = true
      try {
        const [rv, rm] = await Promise.all([
          Transportador.getVeiculos(this.idParticipante),
          Transportador.getMotoristas(this.idParticipante)
        ])
        this.veiculos = (rv.data || [])
          .filter(v => v && v.veiculo && v.veiculo.id)
          .map(v => ({
            idVeiculo: v.veiculo.id,
            // Eixos e composicao veicular decidem a tabela e a linha do
            // piso ANTT, por isso vao junto e aparecem no rotulo.
            eixos: v.veiculo.qtdEixos || null,
            composicaoVeicular: !!v.veiculo.composicaoVeicular,
            rotulo: v.veiculo.placa +
              (v.veiculo.qtdEixos ? ' · ' + v.veiculo.qtdEixos + ' eixos' : '') +
              (v.veiculo.rntrc ? ' · RNTRC ' + v.veiculo.rntrc : '')
          }))
        this.motoristas = (rm.data || [])
          .filter(m => m && m.motorista && m.motorista.id)
          .map(m => ({
            idMotorista: m.motorista.id,
            rotulo: m.motorista.nome + (m.motorista.cpf ? ' · ' + m.motorista.cpf : '')
          }))
      } catch (e) {
        this.erro = 'Não foi possível carregar veículos e motoristas. ' + this.msgErro(e)
      } finally {
        this.carregandoCadastros = false
      }
    },

    limparPrevia () {
      this.previa = null
    },

    //
    // O piso depende do veiculo (eixos e composicao), da distancia e do
    // frete. Mexer em qualquer um deles invalida o numero, e a previa
    // junto - ela carrega o piso para o banco.
    //
    limparPiso () {
      this.piso = null
      this.previa = null
    },

    conferirPisoAntt () {
      const v = this.veiculoSelecionado
      if (!v) return

      this.piso = conferirPiso({
        composicaoVeicular: v.composicaoVeicular,
        eixos: v.eixos,
        distanciaKm: this.distanciaKm,
        valorFrete: this.valorFrete
      })
      this.previa = null
    },

    async verPrevia () {
      this.carregandoPrevia = true
      this.erro = ''
      try {
        const r = await MdfeDistribuicaoService.previa(this.idLote, this.corpo)
        this.previa = r.data || null
      } catch (e) {
        this.previa = null
        this.erro = this.msgErro(e)
      } finally {
        this.carregandoPrevia = false
      }
    },

    //
    // Confirmacao em window.confirm, igual ao envio do CIOT: e o ultimo
    // ponto antes de o documento existir na SEFAZ, e nao ha desfazer.
    //
    async emitir () {
      if (!this.podeEmitir) return

      const ok = window.confirm(
        'Emitir o MDF-e de ' + ((this.grupo && this.grupo.nome) || 'rota') +
        ' com ' + this.chaves.length + ' CT-e?\n\n' +
        'Isto cria um documento fiscal autorizado na SEFAZ e gasta um número de MDF-e. ' +
        'Não existe desfazer: para anular, só cancelamento ou encerramento.')
      if (!ok) return

      this.emitindo = true
      this.erro = ''
      try {
        const r = await MdfeDistribuicaoService.emitir(this.idLote, this.corpo)
        this.emitido = r.data || null
        this.previa = null
      } catch (e) {
        this.erro = this.msgErro(e)
      } finally {
        this.emitindo = false
      }
    },

    msgErro (e) {
      const d = e && e.response && e.response.data
      return (d && (d.mensagem || d.message || d.erro)) ||
        (e && e.message) || 'erro desconhecido'
    },

    peso (v) { return formatarPeso(v) },
    moeda (v) { return formatarMoeda(v) }
  }
}
</script>

<style scoped>
/*
  Mesmo modelo da tela: o #app do App.vue tem text-align center, que
  desce por heranca.
*/
.corpo { text-align: left; }

.bloco-titulo {
  font-size: 0.8rem; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; color: #475569; margin-bottom: 6px;
}
.ajuda { font-size: 0.78rem; color: #64748b; line-height: 1.4; }
.sub { font-size: 0.75rem; color: #64748b; }
.fila { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }

.aviso {
  display: flex; gap: 8px; align-items: flex-start;
  border-radius: 6px; padding: 10px 12px; font-size: 0.82rem; line-height: 1.45;
}
.aviso .ic { flex: 0 0 auto; }
.aviso.info { background: #eff6ff; color: #1e3a8a; }
.aviso.erro { background: #fef2f2; color: #991b1b; }
.aviso.ok { background: #ecfdf5; color: #065f46; }
.aviso.atencao { background: #fffbeb; color: #92400e; }
.lista { margin: 4px 0 0 0; padding-left: 18px; }
.lista li { margin-bottom: 2px; }

.totais { display: flex; flex-wrap: wrap; gap: 8px; }
.total {
  flex: 1 1 140px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 10px;
}
.total.az { border-color: #bfdbfe; background: #eff6ff; }
.total .r { font-size: 0.72rem; color: #64748b; text-transform: uppercase; }
.total .v { font-size: 1.02rem; font-weight: 600; }

.chave { font-family: 'Courier New', monospace; word-break: break-all; }

.json-preview {
  background: #0b1220; color: #e2e8f0; padding: 12px; border-radius: 4px;
  font-family: 'Courier New', monospace; font-size: 11.5px; line-height: 1.5;
  max-height: 320px; overflow: auto; white-space: pre-wrap; word-break: break-word;
}
</style>
